import { useState, type ReactNode } from 'react';

import { ClassListTools } from '../results/ClassListTools';
import { filterByName } from '../results/nameSearch';
import type { ResultDetailTab } from '../results/sessions';
import { ExamQuestionsList } from './ExamQuestionsList';
import { durationSeconds, formatDuration, formatScore, RANK_BADGE_COLOR, type ExamClassRow, type ExamResults, type ExamRowStatus } from './examResults';

const STATUS: Record<ExamRowStatus, { label: string; className: string }> = {
  submitted: { label: 'Đã nộp', className: 'hw-status hw-status--marked' },
  doing: { label: 'Đang làm', className: 'hw-status hw-status--submitted' },
  idle: { label: 'Chưa làm', className: 'hw-status hw-status--missing' },
};

const SUB_TABS: Array<{ id: ResultDetailTab; label: string }> = [
  { id: 'students', label: 'Theo xếp hạng' },
  { id: 'questions', label: 'Theo câu hỏi' },
];

function StudentRow({ row, active, onOpen }: { row: ExamClassRow; active: boolean; onOpen: (code: string) => void }) {
  const status = STATUS[row.status];
  const submitted = row.status === 'submitted';
  const body = (
    <>
      <span className={row.rank !== null ? 'results-row__rank' : 'results-row__rank results-row__rank--none'} style={row.rank !== null ? { background: RANK_BADGE_COLOR } : undefined}>{row.rank !== null ? row.rank : '-'}</span>
      <span className="results-row__main">
        <span className="results-row__name">{row.name}</span>
        <span className="results-row__sub">
          <span className={status.className}>{status.label}</span>
          {submitted && row.submitTime ? <span className="results-row__time">{formatDuration(durationSeconds(row))}</span> : null}
        </span>
      </span>
      <span className="results-row__end">
        {row.score !== null ? <span className="results-row__score">{formatScore(row.score)}</span> : null}
        {submitted ? <span className="results-row__hint">{active ? 'Đang xem' : 'Xem bài làm'}</span> : null}
      </span>
    </>
  );
  return (
    <li className={active ? 'results-row is-active' : 'results-row'}>
      {submitted
        ? <button type="button" className="results-row__inner" aria-label={`${row.name}, ${row.score !== null ? `${formatScore(row.score)} điểm, ` : ''}mở bài làm lên bảng`} aria-current={active ? 'true' : undefined} onClick={() => onOpen(row.code)}>{body}</button>
        : <div className="results-row__inner">{body}</div>}
    </li>
  );
}

export interface ExamDetailTabProps {
  results: ExamResults;
  tab: ResultDetailTab;
  onTab: (tab: ResultDetailTab) => void;
  /** The student whose sheet is on the board. */
  activeCode: string | null;
  onOpenStudent: (code: string) => void;
  onReload: () => void;
  reloading: boolean;
  questionNote: ReactNode;
}

// Chi tiết: the whole class by rank, or the exam question by question
export function ExamDetailTab({ results, tab, onTab, activeCode, onOpenStudent, onReload, reloading, questionNote }: ExamDetailTabProps) {
  const [query, setQuery] = useState('');
  const rows = filterByName(results.classList, query, (row) => `${row.name} ${row.code}`);

  return (
    <>
      <div className="results-subtabs" role="tablist" aria-label="Xem chi tiết">
        {SUB_TABS.map((item) => (
          <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} className={tab === item.id ? 'results-subtabs__tab is-active' : 'results-subtabs__tab'} onClick={() => onTab(item.id)}>{item.label}</button>
        ))}
      </div>
      {tab === 'questions' ? <ExamQuestionsList questions={results.questions} questionNote={questionNote} /> : (
        <section className="quiz-results__block">
          <ClassListTools query={query} onQuery={setQuery} onReload={onReload} reloading={reloading} />
          <div className="results-list__head"><span>{query.trim() ? `${rows.length} / ${results.total} học sinh` : `${results.total} học sinh`}</span><span>Điểm</span></div>
          {rows.length === 0 ? <p className="quiz-results__empty">{results.total === 0 ? 'Lớp chưa có học sinh.' : 'Không có học sinh nào khớp tên này.'}</p> : (
            <ul className="results-list">
              {rows.map((row) => <StudentRow key={row.code} row={row} active={row.code === activeCode} onOpen={onOpenStudent} />)}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
